import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Actualizar el estado para mostrar la UI de error
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Registrar el error
    this.setState({
      error,
      errorInfo
    });

    // Aquí podrías enviar el error a un servicio de logging
    // logErrorToService(error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // UI de error personalizada
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-container">
            <div className="error-boundary-icon">
              ⚠️
            </div>
            <h1 className="error-boundary-title">
              ¡Oops! Algo salió mal
            </h1>
            <p className="error-boundary-message">
              Ha ocurrido un error inesperado. No te preocupes, nuestro equipo ha sido notificado.
            </p>
            
            <div className="error-boundary-actions">
              <button 
                className="btn-primary" 
                onClick={this.handleReload}
              >
                🔄 Recargar página
              </button>
              <button 
                className="btn-secondary" 
                onClick={this.handleGoHome}
              >
                🏠 Ir al inicio
              </button>
            </div>

            {/* Mostrar detalles del error solo en desarrollo */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Detalles del error (Solo en desarrollo)</summary>
                <div className="error-boundary-stack">
                  <h3>Error:</h3>
                  <pre>{this.state.error.toString()}</pre>
                  
                  {this.state.errorInfo && (
                    <>
                      <h3>Component Stack:</h3>
                      <pre>{this.state.errorInfo.componentStack}</pre>
                      
                      <h3>Stack Trace:</h3>
                      <pre>{this.state.error.stack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

// CSS en línea para asegurar que los estilos estén disponibles incluso si hay errores CSS
const errorBoundaryStyles = `
  .error-boundary {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    padding: 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  }

  .error-boundary-container {
    max-width: 500px;
    text-align: center;
    background: white;
    padding: 40px;
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .error-boundary-icon {
    font-size: 4rem;
    margin-bottom: 20px;
  }

  .error-boundary-title {
    color: #2d3748;
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 16px;
  }

  .error-boundary-message {
    color: #718096;
    font-size: 1.1rem;
    margin-bottom: 32px;
    line-height: 1.6;
  }

  .error-boundary-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-bottom: 20px;
  }

  .error-boundary-actions .btn-primary {
    background: #667eea;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s ease;
  }

  .error-boundary-actions .btn-primary:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }

  .error-boundary-actions .btn-secondary {
    background: #e2e8f0;
    color: #4a5568;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s ease;
  }

  .error-boundary-actions .btn-secondary:hover {
    background: #cbd5e0;
    transform: translateY(-1px);
  }

  .error-boundary-details {
    margin-top: 20px;
    text-align: left;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
  }

  .error-boundary-details summary {
    cursor: pointer;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 12px;
  }

  .error-boundary-stack {
    color: #2d3748;
  }

  .error-boundary-stack h3 {
    margin: 16px 0 8px 0;
    font-size: 1rem;
    color: #c53030;
  }

  .error-boundary-stack pre {
    background: #1a202c;
    color: #f7fafc;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 0.8rem;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    .error-boundary-container {
      padding: 24px;
      margin: 20px;
    }

    .error-boundary-actions {
      flex-direction: column;
    }

    .error-boundary-actions .btn-primary,
    .error-boundary-actions .btn-secondary {
      width: 100%;
    }
  }
`;

// Inyectar estilos en el head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = errorBoundaryStyles;
  document.head.appendChild(styleElement);
} 